import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import _pick from 'lodash/pick';
import { ActionResultBoolean } from '@portal/models/actionResultBoolean';
import { ActionResultEnumMapProfileTypeListRoleShared } from '@portal/models/actionResultEnumMapProfileTypeListRoleShared';
import type { ActionResultLegalBuyerResponse } from '@portal/models/actionResultLegalBuyerResponse';
import { ActionResultLegalSellerMainInfoShared } from '@portal/models/actionResultLegalSellerMainInfoShared';
import type { ActionResultLegalSellerResponse } from '@portal/models/actionResultLegalSellerResponse';
import { ActionResultListCompanyRegionShared } from '@portal/models/actionResultListCompanyRegionShared';
import { ActionResultListOrganizationTypeShared } from '@portal/models/actionResultListOrganizationTypeShared';
import { ActionResultOperatorShared } from '@portal/models/actionResultOperatorShared';
import { ActionResultString } from '@portal/models/actionResultString';
import { CompanyFullUpdateRequest } from '@portal/models/companyFullUpdateRequest';
import { CompanyRegionShared } from '@portal/models/companyRegionShared';
import { CompanyShared } from '@portal/models/companyShared';
import { FieldStructureShared } from '@portal/models/fieldStructureShared';
import { LegalBuyerResponse } from '@portal/models/legalBuyerResponse';
import { LegalSellerMainInfoShared } from '@portal/models/legalSellerMainInfoShared';
import { LegalSellerResponse } from '@portal/models/legalSellerResponse';
import { LegalSellerUpdateRequest } from '@portal/models/legalSellerUpdateRequest';
import { OperatorShared } from '@portal/models/operatorShared';
import { OrganizationTypeShared } from '@portal/models/organizationTypeShared';
import { RoleSaveRequest } from '@portal/models/roleSaveRequest';
import { RoleShared } from '@portal/models/roleShared';
import { SaveAdditionalFieldRequest } from '@portal/models/saveAdditionalFieldRequest';
import { companiesClientContext } from '@portal/api-endpoints';
import { BuilderInfoService } from '@portal/builder';
import { ActionResultMapStringField } from '@portal/components';
import {
  ContentTypeEnum,
  EnumsTranslation,
  Responses,
  cleanEmptyValues,
  getEnumsTranslation,
  getHttpParams,
  getMultipartFormData,
  replaceId,
} from '@portal/core';
import { UserWithPrivileges } from '../auth.interfaces';
import { AuthService } from '../auth.service';
import { AvatarService } from '../avatar/avatar.service';
import { clientContext } from '../client.context';
import { HTTP_ERROR_HANDLER_OPTIONS } from '../error-handler';
import ProfileTypeEnum = RoleSaveRequest.ProfileTypeEnum;
import ActionResultMapStringFieldStructureShared = ActionResultMapStringField.ActionResultMapStringFieldStructureShared;
import EntityTypeEnum = SaveAdditionalFieldRequest.EntityTypeEnum;
import StatusEnum = CompanyShared.StatusEnum;

@Injectable({
  providedIn: 'root',
})
export class AgentService extends AvatarService {
  private readonly http = inject(HttpClient);
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly builderInfoService = inject(BuilderInfoService);

  getAgentShort(): Observable<LegalSellerMainInfoShared> {
    return this.authService.getUserInfo().pipe(
      filter((user: UserWithPrivileges) => user.profileType === ProfileTypeEnum.LEGAL_SELLER),
      switchMap(() => this.http.get<ActionResultLegalSellerMainInfoShared>(clientContext.agentShort)),
      tap(r => {
        this.setAvatar(r.value.logo);
      }),
      map(r => r.value),
    );
  }

  // #region operator

  // TODO: move to own service
  getAgentById(id: number, includeCompany = true, isLegalSeller = true): Observable<LegalSellerResponse> {
    const url = replaceId(isLegalSeller ? clientContext.legalSellerById : clientContext.commonSellerById, id);
    const paramsObj = {
      includeMembers: true,
      includeAdditionalFields: true,
      includeCompany: includeCompany.toString(),
      includeAllLangs: true,
    };
    const params = getHttpParams(paramsObj);

    return this.http.get<ActionResultLegalSellerResponse>(url, { params }).pipe(map(r => r.value));
  }

  updateFullCompany(id: number, company: CompanyFullUpdateRequest): Observable<ActionResultString> {
    const context = new HttpContext().set(HTTP_ERROR_HANDLER_OPTIONS, () => ({
      skip: (err: HttpResponse<Responses>) => err.body.errorCode === 'DUPLICATE_VALUE',
    }));

    const url = replaceId(clientContext.updateCompanyById, id);

    return this.http.put<ActionResultString>(url, company, { context });
  }

  updateLegalSellerById(id: number, seller: LegalSellerUpdateRequest): Observable<ActionResultBoolean> {
    const url = replaceId(clientContext.legalSellerById, id);

    return this.http.put<ActionResultBoolean>(url, cleanEmptyValues(seller));
  }

  getRoles(types: ProfileTypeEnum[]): Observable<Record<string, RoleShared[]>> {
    const params = types.reduce((acc, t) => acc.append('types', t), getHttpParams({}));

    return this.http
      .get<ActionResultEnumMapProfileTypeListRoleShared>(clientContext.roles, { params })
      .pipe(map(r => r.value));
  }
  // #endregion

  getAgent(profileType: ProfileTypeEnum, sellerId: number): Observable<LegalSellerResponse> {
    const url =
      profileType === ProfileTypeEnum.COMMON_SELLER
        ? replaceId(clientContext.COMMON_SELLER, sellerId)
        : clientContext.LEGAL_SELLER;

    return this.http
      .get<ActionResultLegalSellerResponse>(url, {
        params: getHttpParams({
          includeUser: true,
          includeCompany: true,
          includeContent: true,
          includeAdditionalFields: true,
          includeAllLangs: true,
        }),
      })
      .pipe(map(r => r.value));
  }

  loadBuyerAgent(id: number): Observable<LegalBuyerResponse> {
    const params = getHttpParams({ includeCompany: true, includeAllLangs: true });
    return this.http
      .get<ActionResultLegalBuyerResponse>(replaceId(clientContext.legalBuyerById, id), { params })
      .pipe(map(r => r.value));
  }

  changeSellerStatus(id: number, status: StatusEnum): Observable<ActionResultBoolean> {
    const url = replaceId(companiesClientContext.seller.status, id);

    return this.http.put<ActionResultBoolean>(url, JSON.stringify(status), { headers: getJSONContentType() });
  }

  updateCompany(company: CompanyFullUpdateRequest) {
    return this.http.put<ActionResultBoolean>(clientContext.updateCompany, company);
  }

  updateAgent(agent: LegalSellerUpdateRequest, id: number, isLegalSeller = true): Observable<ActionResultBoolean> {
    return this.http.put<ActionResultBoolean>(
      replaceId(isLegalSeller ? clientContext.legalSellerById : clientContext.commonSellerById, id),
      cleanEmptyValues(agent),
    );
  }

  getCompanyRegions(): Observable<CompanyRegionShared[]> {
    return this.http.get<ActionResultListCompanyRegionShared>(clientContext.companyRegions).pipe(map(r => r.value));
  }

  getOperatorInfo(): Observable<OperatorShared> {
    return this.http.get<ActionResultOperatorShared>(clientContext.operatorInfo).pipe(map(r => r.value));
  }

  getOrganizationTypes(): Observable<OrganizationTypeShared[]> {
    return this.http
      .get<ActionResultListOrganizationTypeShared>(clientContext.organizationTypes)
      .pipe(map(r => r.value));
  }

  getTaxOptions() {
    return getEnumsTranslation(this.translateService.get('companies.tax'));
  }

  getLanguageOptions(): Observable<EnumsTranslation> {
    const languages = this.builderInfoService.availableLangs;
    const translations = this.translateService
      .get('availableLanguages')
      .pipe(map(t => (languages?.length ? _pick(t, languages) : t)));

    return getEnumsTranslation(translations);
  }

  getTaxationSystemOptions() {
    return getEnumsTranslation(this.translateService.get('companies.taxationSystem'));
  }

  getCompanyProductTypes() {
    return getEnumsTranslation(this.translateService.get('companies.companyProductTypes'));
  }

  updateAvatar(logo: File): Observable<ActionResultBoolean> {
    return this.http.patch<ActionResultBoolean>(
      clientContext.updateLogo,
      getMultipartFormData([
        { name: 'image', value: logo },
        { name: 'request', value: {} },
      ]),
      { headers: { 'Content-Type': ContentTypeEnum.MultipartForm } },
    );
  }

  getFieldsStructure(
    entityType: EntityTypeEnum = EntityTypeEnum.MERCHANT,
  ): Observable<Record<string, FieldStructureShared>> {
    const params = getHttpParams({
      entityType,
    });

    return this.http
      .get<ActionResultMapStringFieldStructureShared>(companiesClientContext.fieldsStructure, { params })
      .pipe(map(r => r.value));
  }
}

function getJSONContentType() {
  return { 'Content-type': 'application/json' };
}
